import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const THEME_STORAGE_KEY = "salesworx-theme";

const ThemeContext = createContext({
  theme: "system",
  setTheme: () => {},
  activeMode: "light",
  isDark: false,
});

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
      }
    } catch {
      // Fallback if localStorage is inaccessible
    }
    return "system";
  });

  const [activeMode, setActiveMode] = useState("light");
  const isFirstRender = useRef(true);

  useEffect(() => {
    const root = document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = (animate = false) => {
      let resolvedMode = "light";
      if (theme === "dark") {
        resolvedMode = "dark";
      } else if (theme === "light") {
        resolvedMode = "light";
      } else {
        resolvedMode = mediaQuery.matches ? "dark" : "light";
      }

      setActiveMode(resolvedMode);

      const updateDOM = () => {
        if (resolvedMode === "dark") {
          root.classList.add("dark");
          root.setAttribute("data-theme", "dark");
        } else {
          root.classList.remove("dark");
          root.setAttribute("data-theme", "light");
        }
      };

      if (!animate || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        updateDOM();
        return;
      }

      if (typeof document.startViewTransition === "function") {
        document.startViewTransition(() => {
          updateDOM();
        });
      } else {
        root.classList.add("theme-transitioning");
        updateDOM();
        window.setTimeout(() => {
          root.classList.remove("theme-transitioning");
        }, 300);
      }
    };

    if (isFirstRender.current) {
      isFirstRender.current = false;
      applyTheme(false);
    } else {
      applyTheme(true);
    }

    const handleSystemChange = () => {
      if (theme === "system") {
        applyTheme(true);
      }
    };

    mediaQuery.addEventListener("change", handleSystemChange);
    return () => mediaQuery.removeEventListener("change", handleSystemChange);
  }, [theme]);

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch {
      // Ignore write errors
    }
  };

  const isDark = activeMode === "dark";

  return (
    <ThemeContext.Provider value={{ theme, setTheme, activeMode, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeProvider;
